// src/admin/layout/AdminLayout.jsx
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import logoMark from "../../logo.png";

function normalize(s) {
  return String(s || "").toLowerCase().trim();
}

function fullNameFromContact(c) {
  const first = c?.first_name || "";
  const last = c?.last_name || "";
  return `${first} ${last}`.trim() || "Unnamed";
}

export default function AdminLayout({ children }) {
  const [q, setQ] = useState("");
  const [memberships, setMemberships] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [searchLoading, setSearchLoading] = useState(true);
  const [searchErr, setSearchErr] = useState(null);
  const [showSearch, setShowSearch] = useState(false);

  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    let cancelled = false;

    async function loadSearchData() {
      setSearchLoading(true);
      setSearchErr(null);

      try {
        const [
          { data: memData, error: memErr },
          { data: vehData, error: vehErr },
          { data: bkgData, error: bkgErr },
        ] = await Promise.all([
          supabase
            .from("memberships")
            .select(`
              id,
              tier,
              status,
              billing,
              crm_contacts:contact_id (
                id,
                first_name,
                last_name,
                email,
                phone
              )
            `)
            .order("created_at", { ascending: false })
            .limit(500),

          supabase
            .from("vehicles")
            .select(`
              id,
              name,
              category,
              status,
              location,
              make,
              model,
              year,
              sub_category
            `)
            .order("created_at", { ascending: false })
            .limit(500),

          supabase
            .from("bookings")
            .select(`
              id,
              start_date,
              end_date,
              status,
              concierge,
              membership_id,
              vehicle_id,
              memberships:membership_id (
                id,
                crm_contacts:contact_id (
                  id,
                  first_name,
                  last_name,
                  email,
                  phone
                )
              ),
              vehicles:vehicle_id (
                id,
                name,
                category
              )
            `)
            .order("created_at", { ascending: false })
            .limit(500),
        ]);

        if (memErr) throw memErr;
        if (vehErr) throw vehErr;
        if (bkgErr) throw bkgErr;

        if (!cancelled) {
          setMemberships(memData || []);
          setVehicles(vehData || []);
          setBookings(bkgData || []);
        }
      } catch (e) {
        if (!cancelled) {
          setSearchErr(e.message || "Failed to load search data.");
          setMemberships([]);
          setVehicles([]);
          setBookings([]);
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    }

    loadSearchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const results = useMemo(() => {
    const s = normalize(q);
    if (!s) return [];

    const memberResults = memberships
      .filter((m) => {
        const c = m.crm_contacts || {};
        const hay = normalize(
          [
            m.id,
            m.tier,
            m.status,
            m.billing,
            c.first_name,
            c.last_name,
            c.email,
            c.phone,
            fullNameFromContact(c),
          ].join(" ")
        );
        return hay.includes(s);
      })
      .slice(0, 5)
      .map((m) => {
        const c = m.crm_contacts || {};
        return {
          type: "Member",
          id: m.id,
          label: fullNameFromContact(c),
          sub: `${c.email || "—"} • ${c.phone || "—"} • ${m.tier || "—"} • ${m.status || "—"}`,
        };
      });

    const vehicleResults = vehicles
      .filter((v) => {
        const hay = normalize(
          [
            v.id,
            v.name,
            v.category,
            v.status,
            v.location,
            v.make,
            v.model,
            v.year,
            v.sub_category,
          ].join(" ")
        );
        return hay.includes(s);
      })
      .slice(0, 5)
      .map((v) => ({
        type: "Vehicle",
        id: v.id,
        label: v.name || "Unnamed Vehicle",
        sub: `${v.category || "—"} • ${v.location || "—"} • ${v.status || "—"}`,
      }));

    const bookingResults = bookings
      .filter((b) => {
        const c = b?.memberships?.crm_contacts || {};
        const v = b?.vehicles || {};
        const hay = normalize(
          [
            b.id,
            b.start_date,
            b.end_date,
            b.status,
            b.concierge,
            b.membership_id,
            b.vehicle_id,
            fullNameFromContact(c),
            c.email,
            c.phone,
            v.name,
            v.category,
          ].join(" ")
        );
        return hay.includes(s);
      })
      .slice(0, 5)
      .map((b) => {
        const c = b?.memberships?.crm_contacts || {};
        const v = b?.vehicles || {};
        return {
          type: "Booking",
          id: b.id,
          label: `Booking ${String(b.id).slice(0, 8)}`,
          sub: `${b.start_date || "—"} → ${b.end_date || "—"} • ${fullNameFromContact(c)} • ${v.name || "Vehicle"}`,
        };
      });

    return [...memberResults, ...vehicleResults, ...bookingResults].slice(0, 10);
  }, [q, memberships, vehicles, bookings]);

  const goResult = (r) => {
    setQ("");
    setShowSearch(false);

    if (r.type === "Member") {
      nav(`/admin/members/${r.id}`);
      return;
    }

    if (r.type === "Vehicle") {
      nav(`/admin/vehicles/${r.id}`);
      return;
    }

    if (r.type === "Booking") {
      nav(`/admin/bookings/${r.id}`);
    }
  };

  return (
    <div className="admin">
      <aside className="admin__sidebar">
        <div className="admin__brand">
          <div className="admin__brandMark admin__brandMark--logo">
            <img src={logoMark} alt="Try Play Go" />
          </div>
          <div className="admin__brandText">
            <div className="admin__brandName">Try Play Go</div>
            <div className="admin__brandSub">Admin v1</div>
          </div>
        </div>

        <nav className="admin__nav">
          <NavItem to="/admin/dashboard" label="Dashboard" />
          <NavItem to="/admin/members" label="Members" />
          <NavItem to="/admin/vehicles" label="Vehicles" />
          <NavItem to="/admin/bookings" label="Bookings" />
        </nav>

        <br />
        <br />
        <br />
        <br />

        <div className="admin__sidebarFooter">
          <div className="admin__hint">
            Tip: Start with ops-only. Add member portal later.
          </div>
        </div>
      </aside>

      <main className="admin__main">
        <header className="admin__topbar">
          <div className="admin__crumb">
            <span className="admin__crumbMuted">Admin</span>
            <span className="admin__crumbDot">•</span>
            <span className="admin__crumbPath">
              {loc.pathname.replace("/admin/", "") || "dashboard"}
            </span>
          </div>

          <div className="admin__searchWrap">
            <input
              className="admin__search"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setShowSearch(true);
              }}
              onFocus={() => setShowSearch(true)}
              placeholder="Search members, vehicles, bookings…"
            />

            {showSearch && !!q.trim() && (
              <div className="admin__searchMenu">
                {searchLoading ? (
                  <div className="admin__searchSub" style={{ padding: 12 }}>
                    Loading search…
                  </div>
                ) : searchErr ? (
                  <div className="admin__searchSub" style={{ padding: 12 }}>
                    {searchErr}
                  </div>
                ) : results.length ? (
                  results.map((r) => (
                    <button
                      key={`${r.type}_${r.id}`}
                      type="button"
                      className="admin__searchItem"
                      onClick={() => goResult(r)}
                    >
                      <div className="admin__searchItemTop">
                        <span className="admin__pill">{r.type}</span>
                        <span className="admin__searchLabel">{r.label}</span>
                      </div>
                      <div className="admin__searchSub">{r.sub}</div>
                    </button>
                  ))
                ) : (
                  <div className="admin__searchSub" style={{ padding: 12 }}>
                    No matches found.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="admin__user">
            <div className="admin__avatar">RC</div>
            <div className="admin__userMeta">
              <div className="admin__userName">Ryan</div>
              <div className="admin__userSub">Admin</div>
            </div>
          </div>
        </header>

        <div className="admin__content">{children}</div>
      </main>
    </div>
  );
}

function NavItem({ to, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `admin__navLink ${isActive ? "is-active" : ""}`}
      end={to === "/admin/dashboard"}
    >
      {label}
    </NavLink>
  );
}